// ============================================================================
// src/lib/processingSourceLink.js — Processing <-> native planner link helpers
// ----------------------------------------------------------------------------
// Planner-integration lane: source facts arrive on each Processing record as
// the server-side LIVE projection (`record.source`, mig 176) — this module no
// longer resolves facts from client planner collections or stale snapshots.
// What remains client-side is presentation: exact two-way navigation routes and
// the program-specific age/date/count formatting used by the fixed Processing
// tables and drawer.
//
// Route contracts (two-way navigation):
//   broiler -> /broiler/batches/<encoded batch NAME> (the record page resolves
//              by name then pins batch.id; the projection carries the live
//              name — source_id itself is the immutable ppp-v4 batch id).
//   cattle  -> /cattle/batches/<source_id>
//   sheep   -> /sheep/batches/<source_id>
//   pig     -> /pig/batches/<groupId>?trip=<tripId> (focuses the EXACT trip).
// ============================================================================

// The canonical missing-value text: the constitution's '—' (T06). Missing values
// also carry data-source-missing="1" where they render, as a stable hook.
export const NOT_RECORDED = '—';

// The four native planner programs. `source_kind` is widened to `string` on the
// record below because historical and milestone rows carry values outside this
// set, and every branch here falls through to null rather than assuming.
export type ProcessingSourceKind = 'broiler' | 'cattle' | 'sheep' | 'pig';

// A cattle/sheep/pig age span from the server-side projection. Both bounds run
// through parseInt, so a numeric string is as valid as a number.
export type ProcessingAgeRange = {
  min_days?: number | string | null;
  max_days?: number | string | null;
  estimated?: boolean | null;
};

// The LIVE server-side source projection (mig 176) attached to each record.
// `matched: false` marks a record whose native source no longer resolves —
// displayRecordTitle treats that exactly like an absent projection.
export type ProcessingSourceProjection = {
  batch_name?: string | null;
  age_days?: number | string | null;
  age?: ProcessingAgeRange | null;
  matched?: boolean;
  phase?: string | null;
  scheduled_with_processor?: boolean | null;
};

// One entry of the pig record's stored sub-batch attribution. Only `sex` is
// read here; the stored vocabulary is 'Boars'/'Gilts'.
export type ProcessingSubBatchAttribution = {
  sex?: string | null;
};

// Only the fields this presentation module reads — deliberately not the full
// Processing record row. Everything is optional because unmatched historical
// rows and milestones carry almost none of it.
export type ProcessingRecord = {
  source_kind?: string | null;
  source_id?: string | number | null;
  source?: ProcessingSourceProjection | null;
  source_phase?: string | null;
  record_type?: string | null;
  title?: string | null;
  trip_ordinal?: number | string | null;
  sub_batch_attribution?: ProcessingSubBatchAttribution[] | null;
};

// Formats whole days into program-specific text. Returns null for an unusable
// day count, which is why ageRangeText below has to cope with null on each side.
export type DaysFormatter = (days: unknown) => string | null;

// Native planner route for a Processing record, or null when the record has no
// live source (unmatched historical rows, milestones).
export function sourceRouteForRecord(record: ProcessingRecord | null | undefined): string | null {
  if (!record || !record.source_kind || !record.source_id) return null;
  const src = record.source || null;
  if (record.source_kind === 'broiler') {
    const name = src && src.batch_name ? String(src.batch_name) : null;
    return name ? `/broiler/batches/${encodeURIComponent(name)}` : null;
  }
  if (record.source_kind === 'cattle') return `/cattle/batches/${record.source_id}`;
  if (record.source_kind === 'sheep') return `/sheep/batches/${record.source_id}`;
  if (record.source_kind === 'pig') {
    const sep = String(record.source_id).indexOf(':');
    if (sep <= 0) return null;
    const groupId = String(record.source_id).slice(0, sep);
    const tripId = String(record.source_id).slice(sep + 1);
    return `/pig/batches/${groupId}?trip=${encodeURIComponent(tripId)}`;
  }
  return null;
}

// Label for the back-link ("View <program> batch" / "View pig trip").
export function sourceLinkLabel(record: ProcessingRecord | null | undefined): string | null {
  if (!record || !record.source_kind) return null;
  if (record.source_kind === 'pig') return 'View pig trip';
  // Indexed by a runtime source_kind that may be outside the map (historical
  // and milestone rows), which is what the `|| 'source'` fallback is for — so
  // an index signature, not the inferred literal keys, is the accurate type.
  const names: Record<string, string> = {broiler: 'broiler', cattle: 'cattle', sheep: 'sheep'};
  return `View ${names[record.source_kind] || 'source'} batch`;
}

// ── Age / duration formatting ────────────────────────────────────────────────
//
// Each formatter below opens with `parseInt(days as string)`. The assertion is
// there because parseInt is DECLARED to take a string while the projection
// delivers age fields as either a number or a numeric string — and parseInt
// performs ToString on its argument at runtime, so passing a number has always
// been correct. Wrapping the call in String() would be equivalent, but it would
// be an unnecessary edit to production behaviour in a conversion that is meant
// to change types only.

// Whole days -> "Nw Nd" (broiler age from hatch to processing).
export function weeksDaysText(days: unknown): string | null {
  const d = parseInt(days as string);
  if (!Number.isFinite(d) || d < 0) return null;
  return `${Math.floor(d / 7)}w ${d % 7}d`;
}

// Whole days -> "Ny Mm" (cattle/sheep age at processing; mirrors the batch
// record pages' ageAtProcessing formatting).
export function yearsMonthsText(days: unknown): string | null {
  const d = parseInt(days as string);
  if (!Number.isFinite(d) || d < 0) return null;
  const years = Math.floor(d / 365);
  const months = Math.floor((d % 365) / 30);
  return `${years}y ${months}m`;
}

// Whole days -> "Nm Nw" (pig age; mirrors the pig planner's month/week style).
export function monthsWeeksText(days: unknown): string | null {
  const d = parseInt(days as string);
  if (!Number.isFinite(d) || d < 0) return null;
  const months = Math.floor(d / 30);
  const weeks = Math.floor((d % 30) / 7);
  return `${months}m ${weeks}w`;
}

// {min_days, max_days, estimated?} -> range text with a single formatter, or
// null when the range is unusable. min==max collapses to one value.
export function ageRangeText(
  age: ProcessingAgeRange | null | undefined,
  formatDays?: DaysFormatter | null,
): string | null {
  if (!age) return null;
  const min = parseInt(age.min_days as string);
  const max = parseInt(age.max_days as string);
  const fmt = typeof formatDays === 'function' ? formatDays : yearsMonthsText;
  const minText = Number.isFinite(min) ? fmt(min) : null;
  const maxText = Number.isFinite(max) ? fmt(max) : null;
  if (!minText && !maxText) return null;
  const core = minText && maxText && minText !== maxText ? `${minText} – ${maxText}` : maxText || minText;
  return age.estimated ? `${core} (est.)` : core;
}

// Program-aware Age cell for the fixed tables. Broiler ages render from the
// projection's single age_days; cattle/sheep/pig render their age range.
export function recordAgeText(record: ProcessingRecord | null | undefined): string | null {
  if (!record || !record.source) return null;
  const src = record.source;
  if (record.source_kind === 'broiler') return weeksDaysText(src.age_days);
  if (record.source_kind === 'pig') return ageRangeText(src.age, monthsWeeksText);
  return ageRangeText(src.age, yearsMonthsText);
}

// Display helper: any nullish/blank value renders the canonical placeholder.
// Never estimate missing values.
export function displayOrNotRecorded(value: unknown): string {
  if (value === null || value === undefined) return NOT_RECORDED;
  const text = String(value).trim();
  return text === '' ? NOT_RECORDED : text;
}

// ── Drawer display title (presentation-only) ─────────────────────────────────
// Pig planner records render '<batch> · Trip <ordinal>' in the drawer — the
// stored/canonical 'Pig Trip · ' prefix is display noise there (the drawer
// already says Pig, and the schedule keeps its separate Batch/Trip columns).
// Built from the LIVE source batch name plus the immutable trip_ordinal;
// nothing is rewritten server-side and identity is untouched. Fail closed:
// any record without a usable batch name AND ordinal — and every non-pig,
// milestone, historical, or unmatched record — returns the stored title
// byte-identical, never invented text.
export function displayRecordTitle(record: ProcessingRecord | null | undefined): string {
  if (!record) return '';
  if (record.source_kind !== 'pig' || record.record_type !== 'planner_batch') return record.title || '';
  const src = record.source && record.source.matched !== false ? record.source : null;
  const name = src && src.batch_name != null ? String(src.batch_name).trim() : '';
  const ordinal = record.trip_ordinal;
  if (!name || ordinal == null || !Number.isFinite(Number(ordinal))) return record.title || '';
  return `${name} · Trip ${ordinal}`;
}

// Native pig batch cards must use the same immutable numbers as Processing.
// Planned and actual trips share that sequence, including archived records;
// neither a native array index nor a date sort can reconstruct the ordinal.
export function pigTripOrdinalsBySource(
  records: readonly Pick<ProcessingRecord, 'source_kind' | 'source_id' | 'record_type' | 'trip_ordinal'>[],
): Record<string, number> {
  const ordinals: Record<string, number> = Object.create(null) as Record<string, number>;
  for (const record of records) {
    if (record.source_kind !== 'pig' || record.record_type !== 'planner_batch' || !record.source_id) continue;
    if (record.trip_ordinal == null || String(record.trip_ordinal).trim() === '') continue;
    const ordinal = Number(record.trip_ordinal);
    if (Number.isInteger(ordinal) && ordinal >= 0) ordinals[String(record.source_id)] = ordinal;
  }
  return ordinals;
}

// ── Pig trip sex (canonical, read-only) ──────────────────────────────────────
// Planned pig trips are single-sex by the (subBatchId, sex) trip-chain
// contract, and the planner reconcile stamps each pig Processing record's
// sub_batch_attribution from the EXACT linked trip (its immutable
// groupId:tripId identity). This helper only normalizes that stored canonical
// vocabulary ('Boars'/'Gilts', mirroring the server's boar%/gilt% matching)
// to the singular display label. It never guesses: an empty, unknown, or
// conflicting attribution resolves to null so the UI renders '—'.
export function pigTripSexLabel(record: ProcessingRecord | null | undefined): string | null {
  if (!record || record.source_kind !== 'pig') return null;
  const attribution = Array.isArray(record.sub_batch_attribution) ? record.sub_batch_attribution : [];
  const labels = new Set<string>();
  for (const entry of attribution) {
    const raw = String((entry && entry.sex) || '')
      .trim()
      .toLowerCase();
    if (!raw) continue;
    if (raw.startsWith('boar')) labels.add('Boar');
    else if (raw.startsWith('gilt')) labels.add('Gilt');
    else return null; // unknown vocabulary — never guess
  }
  return labels.size === 1 ? [...labels][0] : null;
}

// ── Soft signals (pig planned trips) ─────────────────────────────────────────
// 'Auto-planned' until the native trip is locked/scheduled with the processor,
// then 'Processor scheduled'. Secondary text, NOT lifecycle status.
export function pigPlanSignal(record: ProcessingRecord | null | undefined): string | null {
  if (!record || record.source_kind !== 'pig') return null;
  const src = record.source || {};
  const phase = src.phase || record.source_phase;
  if (phase !== 'planned') return null;
  return src.scheduled_with_processor ? 'Processor scheduled' : 'Auto-planned';
}
